/**
 * Mobile Dashboard Component
 * Version: 6.2.0
 * 
 * Mobile-optimized dashboard with touch-friendly interface
 */
import { useState, useRef } from 'react'
import { usePullToRefresh } from '../hooks'
import './MobileDashboard.css'

interface StatCard {
  id: string
  label: string
  value: string
  change?: number
  icon: string
  color?: string
}

interface QuickAction {
  id: string
  label: string
  icon: string
  color: string
  onClick: () => void
}

interface MobileDashboardProps {
  stats?: StatCard[]
  quickActions?: QuickAction[]
  onRefresh?: () => Promise<void>
  onTrade?: (action: 'buy' | 'sell', symbol: string) => void
}

function MobileDashboard({
  stats = [],
  quickActions = [],
  onRefresh,
  onTrade
}: MobileDashboardProps) {
  const [selectedSymbol, setSelectedSymbol] = useState('NIFTY')
  const dashboardRef = useRef<HTMLDivElement>(null)

  // Pull to refresh hook
  const { pullDistance, isRefreshing, isPulling, progress } = usePullToRefresh({
    onRefresh: async () => {
      // Simulate refresh delay
      await new Promise(resolve => setTimeout(resolve, 1500))
      onRefresh?.()
    },
    threshold: 80,
    resistance: 2
  })

  // Default stats
  const defaultStats: StatCard[] = stats.length > 0 ? stats : [
    { id: 'pnl', label: "Today's P&L", value: '+₹12,450', change: 2.34, icon: '📈', color: 'success' },
    { id: 'trades', label: 'Trades', value: '24', icon: '🔄', color: 'primary' },
    { id: 'winrate', label: 'Win Rate', value: '72%', icon: '🎯', color: 'success' },
    { id: 'bots', label: 'Active Bots', value: '5', icon: '🤖', color: 'primary' },
  ]

  // Default quick actions
  const defaultQuickActions: QuickAction[] = quickActions.length > 0 ? quickActions : [
    { id: 'buy', label: 'Buy', icon: '📈', color: 'var(--success)', onClick: () => onTrade?.('buy', selectedSymbol) },
    { id: 'sell', label: 'Sell', icon: '📉', color: 'var(--danger)', onClick: () => onTrade?.('sell', selectedSymbol) },
    { id: 'bot', label: 'Start Bot', icon: '▶️', color: 'var(--primary)', onClick: () => {} },
    { id: 'signal', label: 'Signals', icon: '🔔', color: 'var(--warning)', onClick: () => {} },
  ]

  const symbols = ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'SENSEX']

  return (
    <div 
      ref={dashboardRef}
      className="mobile-dashboard"
      style={{
        transform: isPulling ? `translateY(${pullDistance}px)` : 'translateY(0)',
        transition: isPulling ? 'none' : 'transform 0.3s ease-out'
      }}
    >
      {/* Pull to Refresh Indicator */}
      <div className={`pull-to-refresh-indicator ${isPulling ? 'active' : ''} ${isRefreshing ? 'refreshing' : ''}`}>
        <div className="ptr-spinner" />
        <span className="ptr-text">
          {isRefreshing ? 'Refreshing...' : isPulling ? 'Release to refresh' : '↓ Pull to refresh'}
        </span>
        {isPulling && !isRefreshing && (
          <div className="ptr-progress" style={{ width: `${progress * 100}%` }} />
        )}
      </div>

      {/* Symbol Selector */}
      <div className="mobile-symbol-selector">
        <div className="symbol-tabs">
          {symbols.map((symbol) => (
            <button
              key={symbol}
              className={`symbol-tab ${selectedSymbol === symbol ? 'active' : ''}`}
              onClick={() => setSelectedSymbol(symbol)}
            >
              {symbol}
            </button>
          ))}
        </div>
      </div>

      {/* Price Card */}
      <div className="mobile-price-card">
        <div className="price-header">
          <span className="symbol-name">{selectedSymbol}</span>
          <span className="price-badge">LIVE</span>
        </div>
        <div className="price-main">
          <span className="current-price">24,892.50</span>
          <span className="price-change positive">+145.30 (+0.59%)</span>
        </div>
        <div className="price-ohlc">
          <div className="ohlc-item">
            <span className="ohlc-label">O</span>
            <span className="ohlc-value">24,780</span>
          </div>
          <div className="ohlc-item">
            <span className="ohlc-label">H</span>
            <span className="ohlc-value">24,950</span>
          </div>
          <div className="ohlc-item">
            <span className="ohlc-label">L</span>
            <span className="ohlc-value">24,720</span>
          </div>
          <div className="ohlc-item">
            <span className="ohlc-label">C</span>
            <span className="ohlc-value">24,892</span>
          </div>
        </div>
      </div>

      {/* Quick Trade Actions */}
      <div className="mobile-quick-trade">
        <button className="quick-trade-btn buy" onClick={() => onTrade?.('buy', selectedSymbol)}>
          <span className="trade-icon">📈</span>
          <span className="trade-label">BUY</span>
          <span className="trade-hint">Long Position</span>
        </button>
        <button className="quick-trade-btn sell" onClick={() => onTrade?.('sell', selectedSymbol)}>
          <span className="trade-icon">📉</span>
          <span className="trade-label">SELL</span>
          <span className="trade-hint">Short Position</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="mobile-stats-grid">
        {defaultStats.map((stat) => (
          <div key={stat.id} className={`mobile-stat-card ${stat.color || ''}`}>
            <div className="stat-icon">{stat.icon}</div>
            <div className="stat-content">
              <span className="stat-label">{stat.label}</span>
              <span className="stat-value">{stat.value}</span>
              {stat.change !== undefined && (
                <span className={`stat-change ${stat.change >= 0 ? 'positive' : 'negative'}`}>
                  {stat.change >= 0 ? '↑' : '↓'} {Math.abs(stat.change)}%
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="mobile-quick-actions">
        <h4 className="section-title">Quick Actions</h4>
        <div className="quick-action-grid">
          {defaultQuickActions.map((action) => (
            <button
              key={action.id}
              className="quick-action-item ripple"
              onClick={action.onClick}
              style={{ '--action-color': action.color } as React.CSSProperties}
            >
              <span className="action-icon">{action.icon}</span>
              <span className="action-label">{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Recent Trades */}
      <div className="mobile-recent-section">
        <div className="section-header">
          <h4 className="section-title">Recent Trades</h4>
          <button className="view-all-btn">View All</button>
        </div>
        <div className="mobile-trade-list">
          <div className="trade-item">
            <div className="trade-info">
              <span className="trade-symbol">NIFTY</span>
              <span className="trade-time">2 min ago</span>
            </div>
            <div className="trade-details">
              <span className="trade-type buy">BUY</span>
              <span className="trade-pnl positive">+₹1,250</span>
            </div>
          </div>
          <div className="trade-item">
            <div className="trade-info">
              <span className="trade-symbol">BANKNIFTY</span>
              <span className="trade-time">15 min ago</span>
            </div>
            <div className="trade-details">
              <span className="trade-type sell">SELL</span>
              <span className="trade-pnl negative">-₹320</span>
            </div>
          </div>
          <div className="trade-item">
            <div className="trade-info">
              <span className="trade-symbol">NIFTY</span>
              <span className="trade-time">1 hour ago</span>
            </div>
            <div className="trade-details">
              <span className="trade-type buy">BUY</span>
              <span className="trade-pnl positive">+₹890</span>
            </div>
          </div>
        </div>
      </div>

      {/* Active Bots */}
      <div className="mobile-bots-section">
        <div className="section-header">
          <h4 className="section-title">Active Bots</h4>
          <button className="view-all-btn">Manage</button>
        </div>
        <div className="mobile-bot-cards">
          <div className="bot-card">
            <div className="bot-header">
              <span className="bot-icon">🤖</span>
              <span className="bot-name">RSI Reversal</span>
              <span className="bot-status active">Active</span>
            </div>
            <div className="bot-stats">
              <span className="bot-pnl positive">+₹4,520</span>
              <span className="bot-trades">12 trades</span>
            </div>
          </div>
          <div className="bot-card">
            <div className="bot-header">
              <span className="bot-icon">🤖</span>
              <span className="bot-name">MACD Crossover</span>
              <span className="bot-status active">Active</span>
            </div>
            <div className="bot-stats">
              <span className="bot-pnl positive">+₹2,180</span>
              <span className="bot-trades">8 trades</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MobileDashboard